import { cn } from '@/lib/utils';
import type { OrderLineItem } from '@/types/domain';

interface OrderItemsCompactProps {
  items: OrderLineItem[] | undefined;
  currency?: string;
  className?: string;
}

function formatCurrency(amount: number, currency = 'GBP'): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(amount);
}

function fulfilment(item: OrderLineItem): { label: string; className: string } {
  const shipped = item.quantity_shipped ?? 0;
  if (shipped >= item.quantity && item.quantity > 0) {
    return { label: 'Shipped', className: 'text-success' };
  }
  if (shipped > 0) {
    return { label: `${shipped}/${item.quantity}`, className: 'text-warning' };
  }
  return { label: 'Pending', className: 'text-muted-foreground' };
}

/**
 * Item list sized for the order sheet.
 *
 * The full page renders a 5-column table (~730px minimum); at sheet width that
 * has to become rows. Unit price and line totals stay on the page — this view
 * answers "what is in this order and has it gone out", not "what does it cost".
 */
export function OrderItemsCompact({ items, currency, className }: OrderItemsCompactProps) {
  if (!items || items.length === 0) {
    return (
      <p className={cn('py-6 text-center text-sm text-muted-foreground', className)}>
        No line items on this order.
      </p>
    );
  }

  return (
    <ul className={cn('divide-y divide-border', className)}>
      {items.map((item) => {
        const state = fulfilment(item);
        return (
          <li key={item.id} className="flex items-start gap-3 py-2.5">
            <div className="min-w-0 flex-1">
              <p className="text-sm leading-snug text-foreground">{item.name}</p>
              {item.sku && (
                <span className="mt-1 inline-block rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">
                  {item.sku}
                </span>
              )}
            </div>
            <div className="shrink-0 text-right">
              <p className="text-sm font-medium tabular-nums text-foreground">
                &times;{item.quantity}
              </p>
              <p className={cn('text-[11px] tabular-nums', state.className)}>{state.label}</p>
            </div>
            <div className="w-20 shrink-0 text-right">
              <p className="text-sm tabular-nums text-muted-foreground">
                {formatCurrency(item.amount ?? 0, currency)}
              </p>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export default OrderItemsCompact;
